'use client';

import React, { useEffect, useState, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ThreatTimelineRow {
  id: string;
  time_label: string;
  critical_count: number;
  high_count: number;
  medium_count: number;
  blocked_count: number;
  recorded_at: string;
}

export interface ThreatFeedItem {
  id: string;
  threat_id: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  threat_type: string;
  target: string;
  source_ip: string;
  country_code: string;
  technique: string;
  status: 'Blocked' | 'Analyzing' | 'Contained' | 'Resolved';
  asset: string;
  occurred_at: string;
}

export interface AuditLogRow {
  id: string;
  log_id: string;
  event_timestamp: string;
  actor: string;
  actor_role: string;
  action: string;
  action_type: 'auth' | 'data' | 'config' | 'threat' | 'admin';
  resource: string;
  ip_address: string;
  location: string;
  risk_level: 'critical' | 'high' | 'medium' | 'low' | 'info';
  mfa_status: 'verified' | 'bypass' | 'failed' | 'n/a';
  session_id: string;
  outcome: 'success' | 'failed' | 'blocked';
}

// ─── useThreatTimeline ────────────────────────────────────────────────────────

export function useThreatTimeline() {
  const [data, setData] = useState<ThreatTimelineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const supabase = createClient();

    const fetchInitial = async () => {
      const { data: rows, error: err } = await supabase
        .from('threat_timeline_events')
        .select('*')
        .order('recorded_at', { ascending: true })
        .limit(24);

      if (err) {
        setError(err.message);
      } else {
        setData(rows || []);
      }
      setLoading(false);
    };

    fetchInitial();

    const channel = supabase
      .channel('threat_timeline_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'threat_timeline_events' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setData((prev) => {
              const updated = [...prev, payload.new as ThreatTimelineRow];
              return updated.slice(-24);
            });
          } else if (payload.eventType === 'UPDATE') {
            setData((prev) =>
              prev.map((row) =>
                row.id === (payload.new as ThreatTimelineRow).id
                  ? (payload.new as ThreatTimelineRow)
                  : row
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setData((prev) =>
              prev.filter((row) => row.id !== (payload.old as ThreatTimelineRow).id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { data, loading, error };
}

// ─── useThreatFeed ────────────────────────────────────────────────────────────

export function useThreatFeed(onNewHighSeverity?: (item: ThreatFeedItem) => void) {
  const [threats, setThreats] = useState<ThreatFeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const callbackRef = React.useRef(onNewHighSeverity);
  callbackRef.current = onNewHighSeverity;

  useEffect(() => {
    const supabase = createClient();

    const fetchInitial = async () => {
      const { data: rows, error: err } = await supabase
        .from('threat_feed_items')
        .select('*')
        .order('occurred_at', { ascending: false })
        .limit(50);

      if (err) {
        setError(err.message);
      } else {
        setThreats(rows || []);
      }
      setLoading(false);
    };

    fetchInitial();

    const channel = supabase
      .channel('threat_feed_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'threat_feed_items' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const newItem = payload.new as ThreatFeedItem;
            setThreats((prev) => [newItem, ...prev]);
            // Fire toast callback for high-severity threats
            if (
              (newItem.severity === 'critical' || newItem.severity === 'high') &&
              callbackRef.current
            ) {
              callbackRef.current(newItem);
            }
          } else if (payload.eventType === 'UPDATE') {
            setThreats((prev) =>
              prev.map((item) =>
                item.id === (payload.new as ThreatFeedItem).id
                  ? (payload.new as ThreatFeedItem)
                  : item
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setThreats((prev) =>
              prev.filter((item) => item.id !== (payload.old as ThreatFeedItem).id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { threats, loading, error };
}

// ─── useAuditLogs ─────────────────────────────────────────────────────────────

export function useAuditLogs() {
  const [logs, setLogs] = useState<AuditLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const supabase = createClient();

    const fetchInitial = async () => {
      const { data: rows, error: err } = await supabase
        .from('audit_logs')
        .select('*')
        .order('event_timestamp', { ascending: false })
        .limit(100);

      if (err) {
        setError(err.message);
      } else {
        setLogs(rows || []);
      }
      setLoading(false);
    };

    fetchInitial();

    const channel = supabase
      .channel('audit_logs_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'audit_logs' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setLogs((prev) => [payload.new as AuditLogRow, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            setLogs((prev) =>
              prev.map((log) =>
                log.id === (payload.new as AuditLogRow).id
                  ? (payload.new as AuditLogRow)
                  : log
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setLogs((prev) =>
              prev.filter((log) => log.id !== (payload.old as AuditLogRow).id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { logs, loading, error };
}
